/**
 * /auth/reset/confirm — Page de finalisation du reset password.
 *
 * Flow :
 *  1. Supabase redirige le user ici avec hash `#access_token=...&refresh_token=...&type=recovery`
 *  2. parseAuthCallbackUrl() extrait les tokens (ou retourne un code d'erreur)
 *  3. supabase.auth.setSession() établit la session
 *  4. Le user saisit un nouveau password (≥12 chars, ≠ email)
 *  5. supabase.auth.updateUser({password}) — le password ne quitte JAMAIS le navigateur
 *  6. Redirect /
 *
 * Sécurité (Chunk 7.C) :
 *  - Aucun appel à apps/api avec le password
 *  - Pas de localStorage/sessionStorage du password
 *  - Pas de console.* (override ESLint pages/auth/**)
 *  - Toasts génériques i18n, jamais d'echo password
 *  - full_name affiché read-only depuis user_metadata si disponible
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { parseAuthCallbackUrl } from "../../lib/auth-callback";
import { authErrorToI18nKey } from "../../lib/auth-error-map";
import { supabase } from "../../lib/supabase";
import { toast } from "../../lib/toast";

import { AuthCardLayout } from "./_AuthCardLayout";
import { AuthPasswordForm } from "./_AuthPasswordForm";

type SessionState =
  | { kind: "loading" }
  | { kind: "ready"; email: string; fullName: string | null }
  | { kind: "error"; code: string };

export function ResetConfirmPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState<SessionState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;

    async function init() {
      const parsed = parseAuthCallbackUrl(window.location.href, window.location.hash);

      if (parsed.error || parsed.type !== "recovery") {
        const code = parsed.error ?? "invalid_link";
        if (!cancelled) setState({ kind: "error", code });
        return;
      }

      const { error: setErr } = await supabase.auth.setSession({
        access_token: parsed.accessToken,
        refresh_token: parsed.refreshToken,
      });

      if (cancelled) return;

      if (setErr) {
        setState({ kind: "error", code: "session_failed" });
        return;
      }

      // Nettoyer le hash (les tokens y traînent)
      window.history.replaceState(null, "", window.location.pathname);

      const { data, error: getErr } = await supabase.auth.getUser();
      if (cancelled) return;

      if (getErr || !data.user?.email) {
        setState({ kind: "error", code: "session_failed" });
        return;
      }

      const fullName =
        (data.user.user_metadata?.["full_name"] as string | undefined) ?? null;

      setState({
        kind: "ready",
        email: data.user.email,
        fullName,
      });
    }

    void init();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSubmit(password: string) {
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        toast.error(t("errors.password.update_failed"));
        return;
      }
      toast.success(t("auth.reset_confirm.success"));
      navigate("/", { replace: true });
    } catch {
      toast.error(t("errors.password.update_failed"));
    }
  }

  if (state.kind === "loading") {
    return (
      <AuthCardLayout title={t("auth.reset_confirm.title")}>
        <p className="text-sm text-slate-500">{t("app.loading")}</p>
      </AuthCardLayout>
    );
  }

  if (state.kind === "error") {
    return (
      <AuthCardLayout title={t("auth.reset_confirm.title")}>
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700"
        >
          {t(authErrorToI18nKey(state.code))}
        </div>
        <div className="mt-4 text-center text-sm">
          <Link to="/auth/reset" className="text-brand-600 hover:underline">
            {t("errors.session.request_new_link")}
          </Link>
        </div>
      </AuthCardLayout>
    );
  }

  return (
    <AuthCardLayout
      title={t("auth.reset_confirm.title")}
      subtitle={t("auth.reset_confirm.subtitle", { email: state.email })}
    >
      {state.fullName && (
        <p className="mb-4 text-sm text-slate-600">
          <span className="font-medium">{t("auth.profile.full_name")} :</span>{" "}
          {state.fullName}
        </p>
      )}

      <AuthPasswordForm
        email={state.email}
        submitLabel={t("auth.reset_confirm.submit")}
        submittingLabel={t("app.loading")}
        onSubmit={handleSubmit}
      />

      <div className="mt-6 text-center text-sm">
        <Link to="/auth/login" className="text-brand-600 hover:underline">
          ← {t("common.back")}
        </Link>
      </div>
    </AuthCardLayout>
  );
}
